import { useState } from 'react';
import { Center, Image, Spinner } from '@chakra-ui/react';
import { SVG_ASSERT_URL } from '@/constants/app-constants';
import { useQueryState, useShowToast } from '@/hooks';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getUnsubscribeDetails, unSubscribe } from './api';
import { UnsubscribeWrapper } from './UnsubscribeWrapper';
import { CheckCircle, Mail05 } from '@kula-ai/icons-core';

export const UnSubscribeRevamp = () => {
  const { queryParams } = useQueryState();
  const [unSubscribed, setUnSubscribed] = useState(false);
  const unSubscribeId = queryParams?.['id'];

  const { data, isLoading } = useQuery({
    queryKey: ['unsubscribeDetails'],
    queryFn: () => getUnsubscribeDetails({ id: unSubscribeId }),
  });

  const unSubscribeMutation = useMutation({ mutationFn: unSubscribe });
  const toast = useShowToast();

  if (isLoading) {
    return (
      <Center h="100vh" flex="1">
        <Spinner color="primary-800" />
      </Center>
    );
  }

  if (
    !unSubscribeId ||
    !data ||
    !['active', 'unsubscribed'].includes(data?.status)
  ) {
    return (
      <Center flex="1">
        <Image src={`${SVG_ASSERT_URL}/assessment/404.svg`} />
      </Center>
    );
  }

  if (data?.status === 'unsubscribed') {
    return (
      <UnsubscribeWrapper
        header="You have already unsubscribed"
        icon={CheckCircle}
        subHeader="If you clicked the unsubscribe link by mistake, feel free to ignore this. If you’re still getting emails after unsubscribing, please contact your recruiter."
      />
    );
  }

  if (unSubscribed) {
    return (
      <UnsubscribeWrapper
        header="You have successfully unsubscribed"
        icon={CheckCircle}
        subHeader="We're sorry to see you go. If you change your mind, feel free to reach out to your recruiter anytime."
      />
    );
  }

  return (
    <UnsubscribeWrapper
      header="Are you sure you want to unsubscribe from this email?"
      icon={Mail05}
      subHeader="By unsubscribing, you’re indicating to the recruiter that you're not interested in this opportunity. You won’t receive additional updates about it."
      showCta={true}
      ctaText="Unsubscribe"
      isCtaLoading={unSubscribeMutation.isPending}
      onCtaClick={async () => {
        const result = await unSubscribeMutation.mutateAsync({
          id: unSubscribeId,
        });
        if (result === 204) {
          setUnSubscribed(true);
        } else {
          toast({ title: 'Unsubscribing failed', status: 'error' });
        }
      }}
    />
  );
};

export default UnSubscribeRevamp;
